import React, { useEffect, useMemo, useState } from 'react';
import { aiAgentApi } from '../../../lib/aiAgentApi';
import { toast } from 'sonner';
import { Search, X, UserPlus, Trash2 } from 'lucide-react';

/**
 * Within-org collaborator picker. Owner-only.
 * Lets the thread owner invite teammates (must share an org via RBAC).
 */
export const InvitePicker = ({ convId, onClose, onChanged }) => {
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [u, c] = await Promise.all([
        aiAgentApi.eligibleCollaborators(query),
        aiAgentApi.listCollaborators(convId),
      ]);
      setUsers(u.users || []);
      setCollaborators(c.collaborators || []);
    } catch {
      toast.error('Could not load teammates');
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [query]);

  const existingIds = useMemo(
    () => new Set((collaborators || []).map(c => c.user_id)),
    [collaborators]
  );

  const invite = async (u) => {
    setBusy(true);
    try {
      await aiAgentApi.addCollaborator(convId, u.id);
      toast.success(`Invited ${u.email || u.name}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invite failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (cid) => {
    setBusy(true);
    try {
      await aiAgentApi.removeCollaborator(convId, cid);
      toast.success('Removed');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="ai-invite-picker"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-[min(540px,94vw)] rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <UserPlus className="h-4 w-4 text-emerald-600" />
            Invite teammates to this chat
          </div>
          <button
            onClick={onClose}
            data-testid="ai-invite-close"
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {collaborators?.length > 0 && (
          <div className="border-b border-slate-100 px-5 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">In this chat</div>
            <ul className="space-y-1">
              {collaborators.map((c) => (
                <li key={c.user_id} className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-slate-50">
                  <div className="min-w-0">
                    <div className="truncate text-sm text-slate-900">{c.name || c.email || c.user_id}</div>
                    <div className="truncate text-[11px] text-slate-500">{c.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.role === 'owner' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                      {c.role}
                    </span>
                    {c.role !== 'owner' && (
                      <button
                        data-testid={`ai-collab-remove-${c.user_id}`}
                        onClick={() => remove(c.user_id)}
                        className="rounded p-1 text-rose-500 hover:bg-rose-50"
                        title="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="px-5 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="ai-invite-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search teammates by name or email…"
              className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-3 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
            />
          </div>
        </div>

        <ul className="max-h-72 overflow-y-auto px-5 pb-4">
          {users.length === 0 && (
            <li className="py-6 text-center text-sm text-slate-500">No teammates found.</li>
          )}
          {users.map((u) => {
            const already = existingIds.has(u.id);
            return (
              <li key={u.id} className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="truncate text-sm text-slate-900">{u.name || u.email}</div>
                  <div className="truncate text-[11px] text-slate-500">{u.email}</div>
                </div>
                <button
                  data-testid={`ai-invite-add-${u.id}`}
                  disabled={already || busy}
                  onClick={() => invite(u)}
                  className={`rounded-md px-2.5 py-1 text-xs font-semibold ${already ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                >
                  {already ? 'Added' : 'Invite'}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
